import { useState, useEffect } from 'react';
import { Search, ZapOff, ArrowLeft } from 'lucide-react';
import { TennisLiveApi, type Fixture } from '../services/TennisLiveApi';
import { SearchBar } from '../components/LiveScore/SearchBar';
import { ShimmerLoading } from '../components/LiveScore/ShimmerLoading';
import { GiroSection } from '../components/Giro/GiroSection';
import { RankingsView } from '../components/Rankings/RankingsView';
import { TournamentDetailsView } from '../components/LiveScore/TournamentDetailsView';
import { DateCarousel } from '../components/LiveScore/DateCarousel';
import { TournamentGroup } from '../components/LiveScore/TournamentGroup';
import giroBackground from '../assets/tile_tennis_v2_resultados.png';
import './GlobalResults.css';

export const GlobalResults = () => {
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [mainMenu, setMainMenu] = useState<'Jogos' | 'Rankings'>('Jogos');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [matchCategoryFilter, setMatchCategoryFilter] = useState('Todas as partidas');
  const [orgFilter, setOrgFilter] = useState('TODOS');
  const [isLiveFilter, setIsLiveFilter] = useState(false);
  
  const [rankingsFilter, setRankingsFilter] = useState<'ATP' | 'WTA'>('ATP');
  const [selectedFixture, setSelectedFixture] = useState<Fixture | undefined>(undefined);
  const [selectedTournamentForDetails, setSelectedTournamentForDetails] = useState<{name: string; tournament_key: string; events: any[]} | null>(null);

  useEffect(() => {
    if (mainMenu === 'Jogos') fetchData();
  }, [mainMenu, selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await TennisLiveApi.getFixturesByDate(selectedDate);
      setFixtures(data);
    } catch (err: any) {
      if (err.message === '429') {
        setError('429');
      } else {
        setError('Ocorreu um erro ao buscar os jogos.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredFixtures = fixtures.filter((fixture) => {
    const homeName = String(fixture.homePlayer?.name || '').toLowerCase();
    const awayName = String(fixture.awayPlayer?.name || '').toLowerCase();
    const tourneyName = String(fixture.tournamentName || '').toLowerCase();
    const q = String(searchQuery || '').toLowerCase();

    const matchesSearch = 
      homeName.includes(q) ||
      awayName.includes(q) ||
      tourneyName.includes(q);
    
    if (!matchesSearch) return false;

    if (matchCategoryFilter !== 'Todas as partidas') {
      if (fixture.matchCategory !== matchCategoryFilter) return false;
    }

    if (orgFilter !== 'TODOS') {
      if (fixture.category !== orgFilter) return false;
    }
    
    if (isLiveFilter) {
      const safeStatus = String(fixture.status || '');
      if (!safeStatus.includes('🔴') && !safeStatus.toLowerCase().includes('set')) return false;
    }

    return true; 
  });

  const groupedFixtures = filteredFixtures.reduce((acc, fix) => {
    if (!acc[fix.tournamentName]) acc[fix.tournamentName] = [];
    acc[fix.tournamentName].push(fix);
    return acc;
  }, {} as Record<string, Fixture[]>);

  const handleMatchClick = (fixture: Fixture) => {
    setSelectedFixture(fixture);
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 100);
  };

  const renderEmptyState = () => {
    if (loading) return null;

    if (error === '429') {
      return (
        <div className="gr-empty-state">
          <ZapOff size={64} className="gr-empty-icon" />
          <h2 className="gr-empty-title gr-empty-title-error">
            Nossos radares estão recarregando!
          </h2>
          <p className="gr-empty-desc">
            Atingimos o limite de requisições na nossa API gratuita de tênis. Os resultados voltarão em breve.
          </p>
        </div>
      );
    }

    if (filteredFixtures.length === 0) {
      return (
        <div className="gr-empty-state">
          <Search size={64} className="gr-empty-icon" />
          <h2 className="gr-empty-title">
            Nenhum jogo encontrado
          </h2>
          <p className="gr-empty-desc">
            Não localizamos nenhuma partida para "{searchQuery}" com os filtros atuais.
          </p>
        </div>
      );
    }

    return null;
  };

  const rankingsOptions = ['ATP', 'WTA'];

  return (
    <div className="gr-container">
      {selectedTournamentForDetails && (
        <TournamentDetailsView
          tournament={selectedTournamentForDetails}
          onClose={() => setSelectedTournamentForDetails(null)}
        />
      )}

      {/* Header Image Background with Glassmorphism */}
      <div className="gr-header-wrapper">
        <img 
          src={giroBackground} 
          alt="Giro de Torneios"
          className="gr-header-bg"
        />
        
        <div className="gr-header-gradient" />
        
        <div className="gr-header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <button onClick={() => window.history.back()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} title="Voltar">
              <ArrowLeft size={24} />
            </button>
            <h1 className="gr-title" style={{ margin: 0 }}>
              Giro de Torneios
            </h1>
          </div>
          <SearchBar 
            value={searchQuery} 
            onChangeText={setSearchQuery} 
            onClear={() => setSearchQuery('')} 
          />
        </div>
      </div>

      {/* Main Menu */}
      <div className="gr-main-menu">
        <button 
          className={`gr-menu-btn ${mainMenu === 'Jogos' ? 'active' : ''}`}
          onClick={() => setMainMenu('Jogos')}
        >
          Jogos
        </button>
        <button 
          className={`gr-menu-btn ${mainMenu === 'Rankings' ? 'active' : ''}`}
          onClick={() => setMainMenu('Rankings')}
        >
          Rankings
        </button>
      </div>

      {/* Sub Menu */}
      <div className="gr-action-bar">
        <div className="gr-filter-container">
          {mainMenu === 'Jogos' ? (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', width: '100%', overflowX: 'auto' }}>
              <select 
                value={matchCategoryFilter} 
                onChange={(e) => { setMatchCategoryFilter(e.target.value); setSelectedFixture(undefined); }}
                style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: '#1e293b', color: '#f8fafc', border: '1px solid #334155', outline: 'none', cursor: 'pointer' }}
              >
                {['Todas as partidas', 'Simples Masculino', 'Simples Feminino', 'Duplas Masculinas', 'Duplas Femininas', 'Duplas Mistas', 'Copa por Equipes', 'Masculino Juvenil', 'Feminino Juvenil'].map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <select 
                className="gr-select-filter"
                value={orgFilter}
                onChange={(e) => {
                  setOrgFilter(e.target.value);
                  setSelectedFixture(undefined);
                }}
                style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: '#1e293b', color: '#f8fafc', border: '1px solid #334155', outline: 'none', cursor: 'pointer' }}
              >
                {['TODOS', 'ATP', 'WTA', 'ITF', 'Exibição'].map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px', background: 'rgba(30, 41, 59, 0.5)', padding: '6px 12px', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <input 
                  type="checkbox" 
                  id="liveFilter"
                  checked={isLiveFilter}
                  onChange={(e) => setIsLiveFilter(e.target.checked)}
                  style={{ cursor: 'pointer', accentColor: '#ef4444' }}
                />
                <label htmlFor="liveFilter" style={{ color: '#cbd5e1', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444' }}></div>
                  Somente Ao Vivo
                </label>
              </div>
            </div>
          ) : (
            rankingsOptions.map(option => (
              <button
                key={option}
                className={`gr-filter-btn ${rankingsFilter === option ? 'active' : 'inactive'}`}
                onClick={() => setRankingsFilter(option as 'ATP' | 'WTA')}
              >
                {option}
              </button>
            ))
          )}
        </div>
      </div>

      {mainMenu === 'Jogos' && (
        <div className="gr-main-content">
          <DateCarousel selectedDate={selectedDate} onSelectDate={(date) => { setSelectedDate(date); setSelectedFixture(undefined); }} />
          
          <div style={{ marginTop: '24px' }}>
            {loading && <ShimmerLoading />}
            
            {!loading && Object.keys(groupedFixtures).sort((a, b) => {
              const getWeight = (name: string) => {
                const n = name.toLowerCase();
                if (n.includes('grand slam') || n.includes('wimbledon') || n.includes('roland garros') || n.includes('us open') || n.includes('australian open')) return 100;
                if (n.includes('atp finals') || n.includes('wta finals')) return 90;
                if (n.includes('1000')) return 80;
                if (n.includes('500')) return 70;
                if (n.includes('250')) return 60;
                if (n.includes('challenger')) return 50;
                if (n.includes('itf') || n.includes('w15') || n.includes('w35') || n.includes('w50') || n.includes('w75') || n.includes('w100') || n.includes('m15') || n.includes('m25') || n.includes('m50')) return 40;
                return 30;
              };
              const wA = getWeight(a);
              const wB = getWeight(b);
              if (wA !== wB) return wB - wA;
              return a.localeCompare(b);
            }).map(tournamentName => (
              <TournamentGroup 
                key={tournamentName} 
                tournamentName={tournamentName} 
                fixtures={groupedFixtures[tournamentName]} 
                onMatchClick={handleMatchClick} 
                onTournamentClick={
                  groupedFixtures[tournamentName]?.[0]?.tournamentKey 
                  ? () => setSelectedTournamentForDetails({
                      name: tournamentName,
                      tournament_key: groupedFixtures[tournamentName][0].tournamentKey as string,
                      events: []
                    })
                  : undefined
                }
              />
            ))}

            {renderEmptyState()}
          </div>

          {selectedFixture ? (
            <div style={{ marginTop: '24px' }}>
              <GiroSection fixture={selectedFixture} />
            </div>
          ) : (
            !loading && filteredFixtures.length > 0 && (
              <div style={{ 
                textAlign: 'center', padding: '24px', color: '#94a3b8', 
                fontSize: '0.9rem', backgroundColor: 'rgba(30,41,59,0.5)',
                borderRadius: '12px', marginTop: '16px', border: '1px dashed #334155'
              }}>
                Selecione uma partida acima para ver o Head to Head.
              </div>
            )
          )}
        </div>
      )}

      {mainMenu === 'Rankings' && (
        <div className="gr-main-content">
          <RankingsView activeFilter={rankingsFilter} searchQuery={searchQuery} />
        </div>
      )}
    </div>
  );
};
